import React, { useState } from "react"
import { useGetDocumentsQuery, useUploadDocumentMutation } from "services/api"
import moment from "moment"

function Documents() {
	const [name, setName] = useState("")
	const [category, setCategory] = useState("")
	const [file, setFile] = useState(null)
	const [uploadSuccess, setUploadSuccess] = useState("")
	const [uploadError, setUploadError] = useState("")

	const { data: documents, error, isLoading, refetch } = useGetDocumentsQuery()
	const [uploadDocument, { isLoading: isUploading }] = useUploadDocumentMutation()

	const handleFileChange = (e) => {
		if (e.target.files[0]) {
			setFile(e.target.files[0])
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setUploadSuccess("")
		setUploadError("")

		if (!name || !category || !file) {
			setUploadError("Please fill in all fields")
			return
		}

		const formData = new FormData()
		formData.append("name", name)
		formData.append("category", category)
		formData.append("document", file)

		try {
			await uploadDocument(formData).unwrap()
			setUploadSuccess("Document uploaded successfully")
			setName("")
			setCategory("")
			setFile(null)
			// Reset file input
			document.getElementById("file-input").value = ""
			refetch()
		} catch (err) {
			setUploadError("Error uploading document")
			console.error(err)
		}
	}

	if (error) return <div className="text-red-600">Error loading documents</div>
	if (isLoading) return <div>Loading...</div>

	return (
		<div>
			<h1 className="text-2xl font-bold mb-6">Document Management</h1>

			{/* Upload Form */}
			<div className="bg-white rounded-lg shadow p-6 mb-8">
				<h2 className="text-xl font-semibold mb-4">Upload New Document</h2>
				
				{uploadSuccess && (
					<div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
						{uploadSuccess}
					</div>
				)}
				
				{uploadError && (
					<div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
						{uploadError}
					</div>
				)}

				<form onSubmit={handleSubmit} className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Document Name
						</label>
						<input
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
							placeholder="e.g., Q4 2024 Meeting Presentation"
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Category
						</label>
						<select
							value={category}
							onChange={(e) => setCategory(e.target.value)}
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500">
							<option value="">Select a category...</option>
							<option value="Monthly Housing Data Chart Books">Monthly Housing Data Chart Books</option>
							<option value="Monthly Sales and Traffic Memos">Monthly Sales and Traffic Memos</option>
							<option value="Quarterly Briefing Presentations">Quarterly Briefing Presentations</option>
							<option value="Submarket Demographics Reports">Submarket Demographics Reports</option>
						</select>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							File
						</label>
						<input
							id="file-input"
							type="file"
							onChange={handleFileChange}
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
							accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
						/>
						<p className="text-xs text-gray-500 mt-1">
							Accepted formats: PDF, Word, Excel, PowerPoint
						</p>
					</div>

					<button
						type="submit"
						disabled={isUploading}
						className="bg-indigo-500 text-white px-6 py-2 rounded-md hover:bg-indigo-600 transition duration-300 disabled:bg-gray-400">
						{isUploading ? "Uploading..." : "Upload Document"}
					</button>
				</form>
			</div>

			{/* Documents List */}
			<div className="bg-white rounded-lg shadow p-6">
				<h2 className="text-xl font-semibold mb-4">Uploaded Documents</h2>
				
				{documents?.data?.length === 0 ? (
					<p className="text-gray-500">No documents uploaded yet.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full table-auto">
							<thead>
								<tr className="border-b">
									<th className="text-left py-2 px-4">Name</th>
									<th className="text-left py-2 px-4">Category</th>
									<th className="text-left py-2 px-4">File Name</th>
									<th className="text-left py-2 px-4">Size</th>
									<th className="text-left py-2 px-4">Uploaded By</th>
									<th className="text-left py-2 px-4">Date</th>
								</tr>
							</thead>
							<tbody>
								{documents?.data?.map((doc) => (
									<tr key={doc.id} className="border-b hover:bg-gray-50">
										<td className="py-2 px-4">{doc.name}</td>
										<td className="py-2 px-4">{doc.category}</td>
										<td className="py-2 px-4">{doc.filename}</td>
										<td className="py-2 px-4">
											{doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(2)} MB` : "N/A"}
										</td>
										<td className="py-2 px-4">{doc.uploaded_by_email}</td>
										<td className="py-2 px-4">
											{moment(doc.created_at).format("MMM D, YYYY")}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	)
}

export default Documents
